import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Grid from "components/primitives/Grid";
import useRequest from "hooks/useRequest";
import TrashIcon from "components/Icons/TrashIcon";
import { useCallback, useState } from "react";
import OneRepMaxService from "services/OneRepMaxService";
import useOneRepMaxChartDataActions from "../../useOneRepMaxChartDataActions";
import { useInfoMessage } from "hooks";
import { useTranslation } from "react-i18next";

interface OneRepMaxTooltipCardActionsProps {
  athleteId: string;
  oneRepMaxId: string;
}

function OneRepMaxTooltipCardActions(props: OneRepMaxTooltipCardActionsProps) {
  const { athleteId, oneRepMaxId } = props;
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const deleteByOneRepMaxId = useRequest(OneRepMaxService.deleteByOneRepMaxId, true);
  const oneRepMaxChartDataActions = useOneRepMaxChartDataActions();
  const successMessage = useInfoMessage({ type: "success" });

  const { t } = useTranslation();

  const handleDelete = useCallback(() => {
    setShowConfirmDelete(true);
  }, []);

  const handleCancel = useCallback(() => {
    setShowConfirmDelete(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    try {
      await deleteByOneRepMaxId(athleteId, oneRepMaxId);
      oneRepMaxChartDataActions.deleteOneRepMaxById({ id: oneRepMaxId });
      successMessage({ message: t("info.one_rep_max_deleted") });
      handleCancel();
    } catch (e) {}
  }, [t, athleteId, oneRepMaxId, deleteByOneRepMaxId, handleCancel, oneRepMaxChartDataActions, successMessage]);

  return (
    <Grid container height={34}>
      <Grid size="grow">
        {showConfirmDelete ? (
          <Grid container alignItems="center" spacing={0.5} justifyContent="flex-end">
            <Grid>
              <Typography variant="caption">{t("global.would_you_like_to_delete_this_value")}</Typography>
            </Grid>
            <Grid>
              <Grid container>
                <Grid>
                  <Button
                    color="error"
                    variant="contained"
                    size="small"
                    onClick={handleConfirmDelete}
                    disableElevation
                    sx={{ textTransform: "capitalize" }}
                  >
                    {t("global.confirm_button_text")}
                  </Button>
                </Grid>
                <Grid>&nbsp;</Grid>
                <Grid>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCancel}
                    disableElevation
                    sx={{ textTransform: "capitalize" }}
                  >
                    {t("global.cancel_button_text")}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        ) : (
          <Grid container justifyContent="flex-end">
            <Grid>
              <IconButton
                color="error"
                size="small"
                onClick={handleDelete}
                aria-label={t("charting.delete_measurement_confirmation_message_aria_label")}
              >
                <TrashIcon />
              </IconButton>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}

export default OneRepMaxTooltipCardActions;
